import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const monteCarloMasters: ChauffeurPage = {
  slug: "monte-carlo-masters",
  title: "Chauffeur privé Monte-Carlo Masters – VTC VIP tennis",
  subtitle:
    "Chauffeur privé pendant le Rolex Monte-Carlo Masters : liaisons club ↔ hôtel ↔ Monaco, navettes hospitalité sponsors et accès au Country Club.",
  keywords: [
    "chauffeur monte-carlo masters",
    "vtc monte-carlo masters",
    "chauffeur rolex monte-carlo masters",
  ],
  hero: {
    title: "Chauffeur privé pendant le Rolex Monte-Carlo Masters",
    subtitle:
      "Mi-avril, le Masters 1000 sur terre battue se joue au Monte-Carlo Country Club, à flanc de colline : un chauffeur pour joueurs, sponsors et invités VIP.",
    keywords: [
      "chauffeur Monte-Carlo Masters",
      "VTC Monte-Carlo Country Club",
      "chauffeur tennis Monaco",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Rolex Monte-Carlo Masters",
    badges: [
      "Club ↔ hôtel ↔ Monaco",
      "Navettes hospitalité",
      "Accès Country Club",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Masters",
    items: [
      {
        title: "Country Club à flanc de colline",
        description:
          "Le Monte-Carlo Country Club se situe à Roquebrune-Cap-Martin, sur les hauteurs : accès routier sinueux et dépose-minute saturée que le chauffeur anticipe.",
      },
      {
        title: "Club ↔ hôtel ↔ Monaco",
        description:
          "Joueurs et invités logent surtout à Monaco : liaisons cadencées entre la Principauté, les hôtels et l'entrée du club pendant la quinzaine.",
      },
      {
        title: "Hospitalité sponsors",
        description:
          "Loges, déjeuners et soirées partenaires : navettes coordonnées pour groupes d'invités tout au long des sessions.",
      },
      {
        title: "Aéroport de Nice",
        description:
          "Suivi de vol et accueil pour les arrivées de joueurs, staff et sponsors mi-avril, avec transfert direct vers Monaco ou le Cap-Martin.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Monte-Carlo Masters",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Monaco / Roquebrune avec suivi de vol pour joueurs, staff et invités sponsors.",
      },
      {
        title: "Liaison club ↔ hôtel",
        text: "Allers-retours cadencés entre les hôtels de Monaco et l'entrée du Monte-Carlo Country Club selon les horaires de sessions.",
      },
      {
        title: "Navettes hospitalité sponsors",
        text: "Transport coordonné de groupes d'invités vers les loges, déjeuners et réceptions partenaires.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour enchaîner sessions, rendez-vous et déplacements dans la Principauté.",
      },
      {
        title: "Soirées & dîners",
        text: "Dîners de partenaires et soirées à Monte-Carlo, avec retour de nuit assuré après les matches du soir.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Masters",
    items: [
      {
        name: "Monte-Carlo Country Club",
        description:
          "Courts en terre battue à Roquebrune-Cap-Martin, juste après la frontière, sur les hauteurs.",
      },
      {
        name: "Monte-Carlo & Casino",
        description:
          "Palaces, loges sponsors et dîners de prestige où logent joueurs et invités.",
      },
      {
        name: "Roquebrune-Cap-Martin",
        description:
          "Hôtels et villas du Cap-Martin à proximité immédiate du club.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, arrivées concentrées mi-avril.",
      },
      {
        name: "Menton & frontière italienne",
        description: "Hébergements complémentaires à l'est de la Principauté.",
      },
    ],
  },
  generic: {
    heading: "Le Rolex Monte-Carlo Masters vu d'un chauffeur",
    subheading:
      "Mi-avril, le Masters 1000 sur terre battue mêle joueurs ATP, sponsors et VIP — sur un site à flanc de colline difficile d'accès.",
    paragraphs: [
      "Chaque mois d'avril, le Rolex Monte-Carlo Masters réunit l'élite du tennis ATP au Monte-Carlo Country Club. Le club n'est pas à Monaco même mais à Roquebrune-Cap-Martin, juste après la frontière, accroché aux hauteurs : routes sinueuses et dépose-minute vite saturée aux heures de session. Un chauffeur qui connaît l'accès vous dépose au plus près et reprend le créneau au bon moment.",
      "Joueurs, staff et invités sponsors arrivent surtout par l'aéroport de Nice Côte d'Azur. Suivi de vol, accueil et transfert direct vers les hôtels de Monaco ou du Cap-Martin, avant les liaisons quotidiennes vers le club.",
      "Le tournoi est aussi une vitrine d'hospitalité : loges, déjeuners et soirées partenaires rythment la quinzaine. Les navettes coordonnées et la mise à disposition gardent le même chauffeur pour les groupes d'invités, avec les retours de nuit après les matches du soir.",
    ],
    bullets: [
      "Accès Country Club maîtrisé",
      "Suivi de vol mi-avril",
      "Navettes hospitalité sponsors",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Monte-Carlo Masters",
    steps: [
      "Indiquez vos dates, votre hôtel à Monaco ou au Cap-Martin et vos horaires de sessions.",
      "Nous calons les liaisons club ↔ hôtel et l'accès au Country Club selon le flux du tournoi.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition et navettes hospitalité pour sessions, loges et dîners partenaires.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pendant le Monte-Carlo Masters ?",
      a: "Réservez en amont : mi-avril, Monaco et le Cap-Martin sont très sollicités par le tournoi. Transmettez vos dates, votre hôtel et vos horaires de sessions pour que nous bloquions un chauffeur dédié sur la quinzaine.",
    },
    {
      q: "Le Monte-Carlo Country Club est-il difficile d'accès ?",
      a: "Le club se trouve à Roquebrune-Cap-Martin, sur les hauteurs juste après la frontière, avec un accès routier sinueux et une dépose-minute vite saturée aux heures de session. Le chauffeur anticipe l'affluence, dépose au plus près de l'entrée et vous reprend au créneau prévu.",
    },
    {
      q: "Gérez-vous les navettes d'hospitalité pour les sponsors ?",
      a: "Oui. Nous coordonnons le transport de groupes d'invités vers les loges, déjeuners et soirées partenaires, avec des liaisons cadencées entre les hôtels de Monaco et le club tout au long du tournoi.",
    },
    {
      q: "Assurez-vous les transferts depuis l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil. Nous assurons les arrivées de joueurs, staff et sponsors entre l'aéroport de Nice et Monaco ou le Cap-Martin, puis les liaisons quotidiennes vers le Country Club.",
    },
  ],
  relatedLinks: [
    {
      label: "Liaison Monaco ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Liaison Cannes ⇄ Monaco",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Réserver un véhicule avec chauffeur",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Monte-Carlo Masters",
    text: "Liaisons club ↔ hôtel ↔ Monaco, navettes hospitalité et accès Country Club : réservez un chauffeur dédié pour le tournoi.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "Chauffeur Monte-Carlo Masters — VTC VIP Country Club | KS Rent Services",
    description:
      "Chauffeur privé pendant le Monte-Carlo Masters : liaisons club ↔ hôtel ↔ Monaco, navettes hospitalité, accès au Country Club et runs aéroport de Nice.",
    canonical: "/chauffeur/monte-carlo-masters",
  },
};

export default monteCarloMasters;
